import { Component, ErrorInfo, ReactNode } from 'react'
import Rollbar, { LogArgument } from 'rollbar'
import ErrorPage from 'components/global/ErrorPage'
import LogRocket from 'logrocket'
import { ReactComponent as JisrError500 } from 'assets/images/Jisr500.svg'
import { ReactComponent as MakenError500 } from 'assets/images/Maken500.svg'
import { labels } from 'utils/whitelabel'

const WEB_WIDGET_OPEN_ERROR_MESSAGE = 'webWidget:on.open does not exist'

type RollbarProps = LogArgument & {
  message?: string
  response?: string
}

type TransformType = {
  sessionURL?: string
}

const isWebWidgetOpenError = (errorMessage: RollbarProps): boolean => {
  return errorMessage?.[1].message?.includes(WEB_WIDGET_OPEN_ERROR_MESSAGE)
}

export const rollBar = new Rollbar({
  accessToken: import.meta.env.REACT_APP_ROLLBAR_TOKEN,
  captureUncaught: true,
  enabled: import.meta.env.MODE !== 'development',
  captureUnhandledRejections: true,
  payload: {
    environment: import.meta.env.REACT_APP_ROLLBAR_ENV,
    client: {
      javascript: {
        source_map_enabled: true,
        code_version:
          import.meta.env.REACT_APP_SEMAPHORE_GIT_TAG_NAME || import.meta.env.REACT_APP_GIT_TAG,
        guess_uncaught_frames: true,
      },
    },
  },
  checkIgnore: (isUncaught, args?: RollbarProps): boolean => {
    if (typeof args === 'object' && Array.isArray(args)) {
      if (args?.[1]?.response || args?.[1]?.message === 'Network Error') {
        return true
      }
      if (isWebWidgetOpenError(args)) {
        return true
      }
    }
    // checkIgnore if its false will report Rollbar log
    return false
  },
  transform: (obj: TransformType): void => {
    return LogRocket.getSessionURL((url) => {
      obj.sessionURL = url
    })
  },
})

type ErrorBoundaryProps = {
  children?: ReactNode
}

type ErrorBoundaryState = {
  hasError: boolean
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state = { hasError: false }

  componentDidCatch = (error: Error, info: ErrorInfo): void => {
    // You can also log the error to rollbaran error reporting service
    rollBar.error(error, info)
    LogRocket?.captureException(error)
  }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      const isMaken = labels.name === 'Maken'
      // You can render any custom fallback UI
      return (
        <ErrorPage
          errorCodeImage={isMaken ? <MakenError500 /> : <JisrError500 />} // change images based on labels
          title="500_title"
          subTitle="500_description"
          isBoundary
        />
      )
    }
    return this.props.children
  }
}

export default ErrorBoundary
